import React from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';

import { replaceTask } from '../store/slices/taskInfoSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { createStdRequest, apiFetch, roundToDeadline, showAlert } from '../utils/utils';

import taskModalStyle from '../assets/jss/crm/components/taskModalStyle.jsx';

import DA from '../store/ReduxAccess';

import Modal from './Modal';
import CrmTextField from './CrmTextField';
import CrmButton from './CrmButton';
import CrmRadio from './CrmRadio';
import KSelect from './KSelect';
import AlertModal from './AlertModal';
import KDatePicker from './KDatePicker';

class TaskModal extends React.Component {
  queuedChanges = [];
  groupNameOptions = [];
  groupIdOptions = [];
  userNameOptions = [];
  userIdOptions = [];

  constructor(props) {
    super(props);
    this.state = this.constructStateFromTask(props.task);
  }

  constructStateFromTask(task) {
    const { activationDate, assignmentDeadline, acceptanceDeadline, completionDeadline } = task;
    const assignByDate = assignmentDeadline;
    const acceptByDate = acceptanceDeadline;
    const completeByDate = completionDeadline || DateTime.local().startOf('day');
    const assignmentPanelCategory = task.assigneeId ? 'person' : 'group';
    const assignmentPanelId = assignmentPanelCategory === 'person' ? task.assigneeId : task.assignedGroupId;
    const assigneeCategory = this.handleCategoryAssignment(task);

    return {
      originalTask: task,
      activationDate,
      description: task.description,
      currentState: task.currentState,
      assigneeId: task.assigneeId,
      assignedGroupId: task.assignedGroupId,
      claimantId: task.claimantId,
      assignmentDeadline,
      assigneeCategory,
      acceptanceDeadline,
      completionDeadline,
      assignByDate,
      acceptByDate,
      completeByDate,
      assignmentPanelCategory,
      assignmentPanelId,
      dummyToggle: false,
    };
  }

  componentDidMount() {
    this.setStateFromTask(this.props.task);
    this.computeGroupOptions(this.props.groups);
    this.computeUserOptions();
  }

  componentDidUpdate(prevProps) {
    if (this.props.task !== prevProps.task) {
      this.setStateFromTask(this.props.task);
      this.computeGroupOptions(this.props.groups);
      this.computeUserOptions();
    }
  }

  setStateFromTask(task) {
    const state = this.constructStateFromTask(task);
    this.setState(state);
  }

  computeGroupOptions(groups = []) {
    this.groupNameOptions = groups.map((g) => g.name);
    this.groupIdOptions = groups.map((g) => g.id);
  }

  computeUserOptions() {
    const users = DA.getAllFirmUsers();
    this.userNameOptions = users.map((u) => u.name);
    this.userIdOptions = users.map((u) => u.personId);
  }

  cancelForm = () => {
    this.setStateFromTask(this.state.originalTask);
    this.props.onClose({ action: 'modalCancelled' });
  };

  acceptForm() {
    this.setState({ dummyToggle: !this.state.dummyToggle }, () => this.acceptFormAfterStateSetting());
  }

  acceptFormAfterStateSetting() {
    // validate the state of the "form"
    const { assignedGroupId, assigneeCategory, currentState, assigneeId } = this.state;
    const validationResult = this.validateFormState();

    if (validationResult.valid) {
      if (
        assigneeCategory === 'no-one' &&
        this.state.originalTask.currentState !== 'Unassigned' &&
        currentState !== 'Claimed'
      ) {
        this.enqueueChanges({ op: 'unassign' });
      } else {
        if (currentState !== 'Completed' && assignedGroupId !== this.state.originalTask.assignedGroupId) {
          this.enqueueChanges({ op: 'assign', assignedGroupId: assignedGroupId, assigneeId: assigneeId });
        }

        if (currentState !== 'Completed' && this.state.originalTask.assigneeId !== this.state.assigneeId) {
          this.enqueueChanges({ op: 'assign', assignedGroupId: assignedGroupId, assigneeId: assigneeId });
        }
      }
      // See whether there was a change to the description, and if so
      //  add that to the queue
      if (this.state.description.trim() !== this.state.originalTask.description) {
        this.enqueueChanges({ op: 'changeDescription', newDescrip: this.state.description.trim() });
      }
      // See whether there were any changes to the time values, and if so
      //  add them to the queue
      if (this.state.activationDate !== this.state.originalTask.activationDate) {
        this.enqueueChanges({ op: 'changeActivationDate', newDate: this.state.activationDate });
      }
      this.enqueueChanges(this.getChangeOpsForDeadlineChanges());
      // Run through queue of changes, and, for each
      //   Call the API to effect the change
      //   On return, call the MODIFY_TASK action producer, which will
      //   call the reducer that will change our UI lists
      this.processTaskChanges(this.state.originalTask);
    } else {
      showAlert(this, validationResult.errorMessage);
    }
    this.props.onClose({ action: 'savingToAllevaDb' });
  }

  processTaskChanges(taskObject) {
    if (this.queuedChanges.length === 0) {
      return;
    }
    const change = this.queuedChanges[0];
    this.queuedChanges = this.queuedChanges.slice(1);
    this.processTaskChange(taskObject, change);
  }

  processTaskChange(taskObject, change) {
    switch (change.op) {
      case 'changeDescription':
        this.processChange4changeDescription(taskObject, change);
        break;
      case 'changeActivationDate':
        this.processChange4changeActivationDate(taskObject, change);
        break;
      case 'changeDeadline':
        this.processChange4changeDeadline(taskObject, change);
        break;
      case 'cancel':
        this.processChange4cancel(taskObject, change);
        break;
      case 'unassign':
        this.processChange4unassign(taskObject, change);
        break;
      case 'assign':
        this.processChange4assign(taskObject, change);
        break;
      case 'markIncomplete':
        this.processChange4markIncomplete(taskObject, change);
        break;
      case 'claim':
        this.processChange4claim(taskObject, change);
        break;
      case 'markComplete':
        this.processChange4markComplete(taskObject, change);
        break;
      default:
        showAlert(this, 'We have no handler defined for change: ' + change.op);
        break;
    }
  }

  processChange4changeDescription(taskObject, change) {
    const apiStruct = { newDescription: change.newDescrip };
    this.processDescriptionChange(taskObject.id, apiStruct);
  }

  processChange4changeActivationDate(taskObject, change) {
    const apiStruct = { date: change.newDate.toISO() };
    this.processActivationDateChange(taskObject.id, apiStruct);
  }

  processChange4changeDeadline(taskObject, change) {
    const apiStruct = { deadlineName: change.name, newDeadline: change.deadline.toISO() };
    this.processDeadlineChange(taskObject.id, apiStruct);
  }

  processChange4assign(taskObject, change) {
    const { assigneeId, assignedGroupId } = change;
    const assigneeEmail = assigneeId > 0 ? this.getEmailForPersonId(assigneeId) : '';

    const apiStruct = {
      newState: 'Assigned',
      userEmail: assignedGroupId > 0 ? '' : assigneeEmail,
      groupId: assignedGroupId,
    };

    this.processStateChange(taskObject.id, apiStruct);
  }

  processChange4unassign(taskObject, change) {
    const apiStruct = {
      newState: 'Unassigned',
    };
    this.processStateChange(taskObject.id, apiStruct);
  }

  processChange4claim(taskObject, change) {
    const assigneeEmail = this.getEmailForPersonId(change.id);
    const apiStruct = {
      newState: 'Claimed',
      userEmail: assigneeEmail,
    };
    this.processStateChange(taskObject.id, apiStruct);
  }

  processChange4markComplete(taskObject, change) {
    const assigneeEmail = this.getEmailForPersonId(change.id);
    const apiStruct = {
      newState: 'Completed',
      userEmail: assigneeEmail,
    };
    this.processStateChange(taskObject.id, apiStruct);
  }

  processChange4markIncomplete(taskObject, change) {
    const apiStruct = {
      newState: 'Claimed',
      userEmail: 0,
    };
    this.processStateChange(taskObject.id, apiStruct);
  }

  processChange4cancel(taskObject, change) {
    const apiStruct = {
      newState: 'Canceled',
      userEmail: 0,
    };
    this.processStateChange(taskObject.id, apiStruct);
  }

  processStateChange(taskId, apiStruct) {
    const uri = `/api/task/${taskId}/setState`;
    this.callApiToProcessChange(taskId, uri, apiStruct);
  }

  processDescriptionChange(taskId, apiStruct) {
    const uri = `/api/task/${taskId}/modifyDescription`;
    this.callApiToProcessChange(taskId, uri, apiStruct);
  }

  processActivationDateChange(taskId, apiStruct) {
    const uri = `/api/task/${taskId}/modifyActivationDate`;
    this.callApiToProcessChange(taskId, uri, apiStruct);
  }

  processDeadlineChange(taskId, apiStruct) {
    const uri = `/api/task/${taskId}/modifyDeadline`;
    this.callApiToProcessChange(taskId, uri, apiStruct);
  }

  callApiToProcessChange(taskId, uri, apiStruct) {
    const request = createStdRequest(uri, 'put', apiStruct);
    const actionOnGoodResponse = (json) => {
      const newTask = json.task;
      // Replace Task in Redux store
      this.props.replaceTask(newTask);
      this.processTaskChanges(newTask);
    };
    // changing all the changes
    apiFetch(this, request, actionOnGoodResponse);
  }

  getChangeOpsForDeadlineChanges() {
    // changeObjects should look like: {op: "changeDeadline" name: "Assignment" deadline: 19375850000}
    const changeOps = [];
    const { assignmentDeadline, acceptanceDeadline, completionDeadline, assignByDate, acceptByDate, completeByDate } =
      this.state;

    if (!assignByDate.equals(assignmentDeadline)) {
      const changeOp = { op: 'changeDeadline', name: 'Assignment', deadline: assignByDate };
      changeOps.push(changeOp);
    }

    if (!acceptByDate.equals(acceptanceDeadline)) {
      const changeOp = { op: 'changeDeadline', name: 'Acceptance', deadline: acceptByDate };
      changeOps.push(changeOp);
    }

    if (!completeByDate.equals(completionDeadline)) {
      const changeOp = { op: 'changeDeadline', name: 'Completion', deadline: completeByDate };
      changeOps.push(changeOp);
    }
    return changeOps;
  }

  getEmailForPersonId(personId) {
    const users = DA.getAllFirmUsers();
    return users.find((u) => u.personId === personId).email;
  }

  validateFormState() {
    // Checks:
    //   * None of the time deltas is now negative
    return this.validateTimeValues();
  }

  oneDay = 24 * 60 * 60 * 1000;

  validateTimeValues() {
    const { currentState, activationDate, assignByDate, acceptByDate, completeByDate } = this.state;
    if (currentState === 'Unassigned' && this.datePrecedes(assignByDate, activationDate)) {
      return { valid: false, errorMessage: 'Your "Assign by" deadline can\'t precede your "Activation" date' };
    }
    if (
      (currentState === 'Unassigned' || currentState === 'Assigned') &&
      this.datePrecedes(acceptByDate, assignByDate)
    ) {
      return { valid: false, errorMessage: 'Your "Accept by" deadline can\'t precede your "Assign by" deadline' };
    }
    if (
      currentState !== 'Completed' &&
      currentState !== 'Canceled' &&
      this.datePrecedes(completeByDate, acceptByDate)
    ) {
      return { valid: false, errorMessage: 'Your "Complete by" deadline can\'t precede your "Accept by" deadline' };
    }
    return { valid: true };
  }

  datePrecedes(date1, date2) {
    return date1.valueOf() < date2.valueOf();
  }

  setTaskCurrentState(stateName, assigneeId, assignedGroupId) {
    const prevState = this.state.currentState;
    this.setState({ currentState: stateName });
    if (stateName === 'Canceled') {
      this.enqueueChanges({ op: 'cancel' });
    }
    if (stateName === 'Unassigned') {
      this.setState({ claimantId: 0 });
      this.setState({ assigneeId: 0 });
      this.enqueueChanges({ op: 'unassign' });
    }
    if (stateName === 'Assigned') {
      this.enqueueChanges({ op: 'assign', assigneeId, assignedGroupId });
    }
    if (stateName === 'Claimed') {
      if (prevState === 'Completed') {
        this.enqueueChanges({ op: 'markIncomplete' });
      } else {
        // prevState === "Assigned"
        const claimantId = this.props.userInfo.personId;
        this.setState({ claimantId });
        this.enqueueChanges({ op: 'claim', id: claimantId });
      }
    }
    if (stateName === 'Completed') {
      const claimantId = this.props.userInfo.personId;
      this.setState({ claimantId });
      this.enqueueChanges({ op: 'markComplete', id: claimantId });
    }
  }

  enqueueChanges(changes) {
    this.queuedChanges = this.queuedChanges.slice().concat(changes);
  }

  cancelTask() {
    this.setTaskCurrentState('Canceled');
    this.acceptForm();
  }

  completeTask = () => {
    // make sure this user is marked as the Claimant of the task
    // mark it completed (does the previous in line)
    this.setTaskCurrentState('Completed');
    // assume this is the last action, and accept the form, closing it.
    this.acceptForm();
  };

  claimTask = () => {
    this.setTaskCurrentState('Claimed');
    this.acceptForm();
  };

  getNameOfSubject(task) {
    if (task) {
      return this.getNameOfSubjectInTask(task);
    } else {
      return null;
    }
  }

  getNameOfSubjectInTask(task) {
    // get person information
    const subjectId = task.subjectId;
    if (subjectId > 0) {
      const person = DA.getPersonById(subjectId);
      if (person) {
        return person.preferredFullName;
      } else {
        return 'Jane Doe';
      }
    }

    // get inquiry information
    const inquiryId = task.inquiryId;
    if (inquiryId > 0) {
      const inquiry = DA.getInquiryById(inquiryId);
      return inquiry ? inquiry.name : 'Unknown Inquiry';
    }

    // get firm information
    const subjectFirmId = task.subjectFirmId;
    if (subjectFirmId > 0) {
      const firm = DA.getFirmById(subjectFirmId);
      return firm ? firm.name : 'Unknown Company';
    }

    return null;
  }

  getCategoryLabel(task) {
    if (task) {
      return this.getCategoryLabelInTask(task);
    } else {
      return null;
    }
  }

  getCategoryLabelInTask(task) {
    const { subjectId, subjectFirmId } = task;
    if (subjectId > 0) {
      const person = DA.getPersonById(subjectId);
      if (person) {
        const cat =
          person.status === 'Client'
            ? 'Active Client'
            : person.status === 'Alum'
            ? 'Alumni'
            : subjectFirmId > 0
            ? 'Referral Contact'
            : person.status;
        return cat;
      } else {
        return 'Unknown Category';
      }
    }

    // get inquiry information
    if (task.inquiryId > 0) {
      return 'Lead';
    }

    // get firm information
    if (subjectFirmId > 0) {
      return 'Referral Company';
    }

    return null;
  }

  validateAssignmentPanel() {
    if (this.state.assignmentPanelId === 0 || this.state.assignmentPanelId === '0') {
      return { valid: false, errorMessage: 'You need to select a person or group to whom to assign the Task' };
    } else {
      return { valid: true };
    }
  }

  setAssignByDate(newDate) {
    const roundedDate = roundToDeadline(newDate);
    this.setState({ assignByDate: roundedDate });
  }

  setAcceptByDate(newDate) {
    const roundedDate = roundToDeadline(newDate);
    this.setState({ acceptByDate: roundedDate });
    this.setAssignByDate(roundedDate);
  }

  setCompleteByDate(newDate) {
    const roundedDate = roundToDeadline(newDate);
    this.setState({ completeByDate: roundedDate });
    this.setAcceptByDate(roundedDate);
  }

  setAssigneeId(newValue) {
    if (this.state.assigneeCategory === 'person') {
      this.setState({ assigneeId: newValue, assignedGroupId: 0 });
    } else {
      this.setState({ assignedGroupId: newValue, assigneeId: this.state.assigneeId });
    }
  }

  selectControlForAssignmentType(classes) {
    switch (this.state.assigneeCategory) {
      case 'person':
        return (
          <KSelect
            fullWidth
            label="Assignee"
            liveOptions={this.userNameOptions}
            liveValues={this.userIdOptions}
            value={this.state.assigneeId}
            onChange={(event) => this.setAssigneeId(event.target.value)}
          />
        );
      case 'group':
        if (this.groupNameOptions.length === 0) {
          return null;
        } else {
          return (
            <KSelect
              fullWidth
              label="Assigned Group"
              liveOptions={this.groupNameOptions}
              liveValues={this.groupIdOptions}
              value={this.groupIdOptions[0] ? this.groupIdOptions[0].value : ''}
              onChange={(event) => this.setAssigneeId(event.target.value)}
            />
          );
        }
      case 'no-one':
      default:
        return <KSelect className={classes.hiddenAssignmentSelect} label="This shouldn't be visible" />;
    }
  }

  handleAssignmentTypeChange = (event) => {
    if (event.target.value === 'person') {
      this.setState({ assignedGroupId: 0 });
    }
    if (event.target.value === 'no-one') {
      this.setState({ currentState: 'Unassigned' });
    } else {
      this.setState({ currentState: 'Assigned' });
    }
    this.setState({ assigneeCategory: event.target.value });
  };

  handleCategoryAssignment = ({ assignedGroupId, currentState }) => {
    if (currentState === 'Unassigned') {
      return 'no-one';
    }
    return assignedGroupId === 0 || currentState === 'Claimed' ? 'person' : 'group';
  };

  render() {
    const { classes, show, task } = this.props;

    let stateInt = -1;
    if (this.state.currentState === 'Unassigned') {
      stateInt = 0;
    } else if (this.state.currentState === 'Assigned') {
      stateInt = 1;
    } else if (this.state.currentState === 'Claimed') {
      stateInt = 2;
    } else if (this.state.currentState === 'Completed') {
      stateInt = 3;
    }

    const taskIsCompleted = task.currentState === 'Completed';

    const completer = taskIsCompleted ? DA.getPersonById(this.state.assigneeId) : null;
    const completerName = completer ? completer.preferredFullName : '';

    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <Modal open={show} title="Modify Task" hideButtons={true} onCancel={() => this.cancelForm()}>
          <Grid container>
            <Grid item xs={12}>
              <div style={{ marginBottom: '12px' }}>
                <CrmTextField
                  className={classes.descriptionField}
                  multiline
                  value={this.state.description}
                  onChange={(event) => this.setState({ description: event.target.value })}
                />
              </div>
            </Grid>
            {task.subjectId || task.inquiryId || task.subjectFirmId ? (
              <Grid item xs={12}>
                <CrmTextField
                  label={this.getCategoryLabel(task)}
                  fullWidth
                  disabled
                  value={this.getNameOfSubject(task)}
                />
              </Grid>
            ) : null}
          </Grid>
          {!taskIsCompleted ? (
            <Grid container style={{ marginTop: '14px' }}>
              <Grid item xs={12}>
                Assign to
                <Grid item xs={1} />
                <Grid item xs={11}>
                  <FormControl variant="standard" component="fieldset" className={classes.formControl}>
                    <RadioGroup
                      row
                      aria-label="Assignment Type"
                      name="assignment-type"
                      className={classes.group}
                      value={this.state.assigneeCategory}
                      onChange={this.handleAssignmentTypeChange}
                    >
                      <FormControlLabel value="person" control={<CrmRadio />} label="person" />
                      {this.groupNameOptions.length > 0 && (
                        <FormControlLabel value="group" control={<CrmRadio />} label="group" />
                      )}
                      <FormControlLabel value="no-one" control={<CrmRadio />} label="unassigned" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Collapse in={this.state.assigneeCategory !== 'no-one'} timeout={400}>
                  <Grid item xs={1} />
                  <Grid item xs={11}>
                    {/* Select Form */}
                    {this.selectControlForAssignmentType(classes)}
                  </Grid>
                </Collapse>
              </Grid>
              <Grid item xs={12} md={6}>
                {stateInt < 3 && (
                  <div>
                    <KDatePicker
                      label="Due"
                      value={this.state.completeByDate}
                      onChange={(newDate) => this.setCompleteByDate(newDate)}
                      renderInput={(params) => <TextField variant="standard" {...params} />}
                    />
                  </div>
                )}
              </Grid>
            </Grid>
          ) : (
            <Grid container>
              <Grid item xs={6}>
                <div className={classes.assignedToHolder}>
                  <CrmTextField label="Completed by" fullWidth disabled value={completerName} />
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                {stateInt < 3 && (
                  <div>
                    <KDatePicker
                      label="Due"
                      value={this.state.completeByDate}
                      onChange={(newDate) => this.setCompleteByDate(newDate)}
                    />
                  </div>
                )}
              </Grid>
            </Grid>
          )}
          <Grid container className={classes.buttonDiv}>
            <Grid item xs={taskIsCompleted ? 5 : 6}>
              {(task.assignedGroupId > 0 && task.currentState === 'Assigned') || task.currentState === 'Unassigned' ? (
                <CrmButton outlined onClick={this.claimTask}>
                  Claim Task
                </CrmButton>
              ) : task.assignedGroupId > 0 && task.currentState === 'Claimed' ? (
                <CrmButton outlined onClick={this.completeTask}>
                  Complete
                </CrmButton>
              ) : taskIsCompleted ? (
                <CrmButton outlined onClick={this.claimTask}>
                  Reopen
                </CrmButton>
              ) : (
                <CrmButton outlined onClick={this.completeTask}>
                  {' '}
                  Complete
                </CrmButton>
              )}
            </Grid>
            <Grid item xs={3}>
              <CrmButton outlined onClick={() => this.cancelTask()}>
                Delete
              </CrmButton>
            </Grid>
            <Grid item xs={2} className={cx({ [classes.saveButtonWithMargin]: taskIsCompleted })}>
              <CrmButton filled onClick={() => this.acceptForm()}>
                Save
              </CrmButton>
            </Grid>
          </Grid>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  taskInfo: state.taskInfo,
});
const actionProducers = { replaceTask, setApiResponseInfo };
export default connect(mapStateToProps, actionProducers)(withStyles(taskModalStyle)(TaskModal));
